/** @type {WeakMap<HTMLElement, AbortController>} */
const boundArrowControllers = new WeakMap();

/**
 * Wire external carousel arrows in a section header to a resource-list slideshow.
 *
 * @param {ParentNode} root - Section or block root containing arrows and slideshow.
 */
export function wireResourceListCarouselArrows(root) {
  if (!root) return;

  const arrowsContainers = root.querySelectorAll('[data-carousel-arrows]');

  for (const container of arrowsContainers) {
    if (!(container instanceof HTMLElement)) continue;

    const slideshow = findResourceListSlideshow(root, container.dataset.slideshowId);
    if (!slideshow || typeof slideshow.select !== 'function') continue;

    const slideshowKey = slideshow.id || slideshow.getAttribute('ref') || '';
    const alreadyBound =
      container.dataset.carouselArrowsWired === 'true' &&
      container.dataset.carouselSlideshowKey === slideshowKey;

    if (alreadyBound) {
      updateCarouselArrowStates(container, slideshow);
      continue;
    }

    boundArrowControllers.get(container)?.abort();

    const controller = new AbortController();
    const { signal } = controller;
    boundArrowControllers.set(container, controller);

    container.dataset.carouselArrowsWired = 'true';
    container.dataset.carouselSlideshowKey = slideshowKey;

    const resolveSlideshow = () =>
      findResourceListSlideshow(root, container.dataset.slideshowId);

    const onSelect = () => {
      const activeSlideshow = resolveSlideshow();
      if (!activeSlideshow || typeof activeSlideshow.select !== 'function') return;
      updateCarouselArrowStates(container, activeSlideshow);
    };

    container.addEventListener(
      'click',
      (event) => {
        const activeSlideshow = resolveSlideshow();
        if (!activeSlideshow || typeof activeSlideshow.select !== 'function') return;

        const prev = event.target.closest('.slideshow-control--previous');
        const next = event.target.closest('.slideshow-control--next');

        if (prev) {
          event.preventDefault();
          event.stopPropagation();
          if (prev.disabled) return;
          const nextIndex = Math.max(0, activeSlideshow.current - 1);
          activeSlideshow.select(nextIndex, event);
          onSelect();
        } else if (next) {
          event.preventDefault();
          event.stopPropagation();
          if (next.disabled) return;
          const slides = activeSlideshow.slides;
          const nextIndex = slides ? Math.min(slides.length - 1, activeSlideshow.current + 1) : 0;
          activeSlideshow.select(nextIndex, event);
          onSelect();
        }
      },
      { signal }
    );

    slideshow.addEventListener('slideshow:select', onSelect, { signal });
    onSelect();
  }
}

/**
 * Retry wiring after custom elements upgrade (e.g. AJAX section inject).
 *
 * @param {ParentNode} root
 */
export function scheduleWireResourceListCarouselArrows(root) {
  if (!root) return;

  wireResourceListCarouselArrows(root);

  customElements.whenDefined('slideshow-component').then(() => {
    wireResourceListCarouselArrows(root);
    requestAnimationFrame(() => wireResourceListCarouselArrows(root));
  });
}

/**
 * @param {ParentNode} root
 * @param {string | undefined} slideshowId
 * @returns {HTMLElement | null}
 */
function findResourceListSlideshow(root, slideshowId) {
  if (slideshowId) {
    const byId = root.querySelector(`#${CSS.escape(slideshowId)}`);
    if (byId) return byId;
  }

  return (
    root.querySelector('.resource-list__carousel > slideshow-component') ||
    root.querySelector('.resource-list__carousel slideshow-component')
  );
}

/**
 * @param {HTMLElement} container
 * @param {HTMLElement & { slides?: HTMLElement[], current: number, infinite?: boolean, visibleSlides?: HTMLElement[] }} slideshow
 */
function updateCarouselArrowStates(container, slideshow) {
  const prev = container.querySelector('.slideshow-control--previous');
  const next = container.querySelector('.slideshow-control--next');
  if (!prev || !next) return;

  const slides = slideshow.slides;
  const current = slideshow.current;
  const infinite = slideshow.infinite;
  const visibleCount = getVisibleCount(slideshow);
  const lastPageStart = slides?.length ? Math.max(0, slides.length - visibleCount) : 0;

  prev.disabled = !infinite && current <= 0;
  next.disabled = !infinite && slides && current >= lastPageStart;
}

/**
 * @param {HTMLElement & { visibleSlides?: HTMLElement[] }} slideshow
 */
function getVisibleCount(slideshow) {
  const visible = slideshow.visibleSlides;
  if (visible?.length > 0) return visible.length;

  const carousel = slideshow.closest('.resource-list__carousel');
  const styles = carousel ? getComputedStyle(carousel) : null;

  if (typeof window !== 'undefined' && window.matchMedia('(width < 750px)').matches) {
    const mobileColumns = Number.parseFloat(styles?.getPropertyValue('--column-count-mobile') || '');
    if (Number.isFinite(mobileColumns) && mobileColumns > 0) {
      return Math.max(1, Math.ceil(mobileColumns));
    }
    return 1;
  }

  const desktopColumns = Number.parseInt(styles?.getPropertyValue('--column-count') || '', 10);
  if (Number.isFinite(desktopColumns) && desktopColumns > 0) {
    return desktopColumns;
  }

  return 4;
}

function initResourceListCarouselArrows() {
  document.querySelectorAll('[data-resource-list-carousel-section]').forEach((section) => {
    scheduleWireResourceListCarouselArrows(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initResourceListCarouselArrows);
} else {
  initResourceListCarouselArrows();
}

document.addEventListener('shopify:section:load', (event) => {
  const sectionRoot =
    document.querySelector(`#shopify-section-${event.detail.sectionId} [data-resource-list-carousel-section]`) ||
    document.querySelector(`#shopify-section-${event.detail.sectionId} product-recommendations`) ||
    document.querySelector(`#shopify-section-${event.detail.sectionId} recently-viewed-products`) ||
    document.querySelector(`#shopify-section-${event.detail.sectionId} featured-collection-tabs`);

  if (sectionRoot) {
    scheduleWireResourceListCarouselArrows(sectionRoot);
  }
});
